import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip, ib, table } from "../../ui/record";
import { archiveModal, area, field, refuse, select } from "../clients/forms";

// Settings › Home Care › Scheduling (brief 1D-2, mockup V["homecare/settings"]("scheduling")):
// how visits are published, confirmed, claimed and matched. Publishing; Confirmations and
// check-in (the numbers 1D-3 uses); Open visits (claiming itself is 1D-3); What caregivers see;
// Availability; Shift patterns (add, edit, archive with a reason, per client); Matching factors
// (rule and weight per factor, add a factor by picking a patient question and a caregiver field);
// Issues bar (which kinds show). Brief 1D-3b: the can't-work cut-off in hours, the check-out
// reminder, check-in distance and grace, the office phone, the can't-work reasons (add, edit,
// archive with a reason) and phone unlock; and the Orientation card (per client type Off, Flag or
// Required, the orientation visit's pay and billing, the minimum oriented backups, backups first,
// the shadow visit's hours). Brief 1D-4: the Checking and validating card (validate clean visits
// automatically, the documentation check per client type, the reasons for correcting a time, the
// late-correction days and the missed-punch pattern count). Every value is data. Admin; every change is in the history.

type Settings = Record<string, unknown> & {
  issues: Record<string, boolean>;
  caregiversSee: "own" | "whole";
  cannotWorkReasons: string[];
  correctionReasons: string[];
  officePhone: string;
};
interface OrientationType {
  id: string;
  name: string;
  orientation: string;
  paid: boolean;
  words: string;
}
interface Answer {
  settings: Settings;
  bounds: Record<string, [number, number]>;
  issueKinds: { key: string; label: string }[];
  rules: { value: string; label: string }[];
  factors: {
    id: string;
    patient: string;
    caregiver: string;
    caregiverKey: string;
    rule: string;
    weight: number;
  }[];
  patientSides: { value: string; label: string }[];
  caregiverSides: { value: string; label: string }[];
  patterns: {
    id: string;
    name: string;
    start: string;
    end: string;
    words: string;
    usedBy: string;
    clientId: string | null;
  }[];
  clients: { id: string; name: string }[];
  orientation: {
    types: OrientationType[];
    orientations: { value: string; label: string }[];
  };
  docChecks: DocChecks;
  texts: { newVisit: string; remindDays: number; emailCopy: boolean };
}
interface DocChecks {
  types: { id: string; name: string }[];
  checks: { key: string; label: string; on: Record<string, boolean> }[];
}

const clock = (t: string) => {
  const h = +t.slice(0, 2);
  return `${h % 12 || 12}:${t.slice(3, 5)} ${h < 12 ? "AM" : "PM"}`;
};

export async function schedulingScreen(host: HTMLElement, ctx: ScreenContext) {
  ctx.crumb("Scheduling");
  const slot = el("div");
  host.append(slot);
  let a: Answer;

  async function load() {
    const res = await api<Answer>("GET", "/api/settings/scheduling");
    if (!res.ok) {
      slot.replaceChildren(
        el("p", { class: "note", role: "alert" }, "Scheduling settings need Admin."),
      );
      return;
    }
    a = res.body;
    draw();
  }

  function draw() {
    const s: Settings = {
      ...a.settings,
      issues: { ...a.settings.issues },
      cannotWorkReasons: [...a.settings.cannotWorkReasons],
      correctionReasons: [...a.settings.correctionReasons],
    };
    const box = (key: string, label: string) => {
      const i = el("input", { type: "checkbox", id: `ss-${key}`, checked: s[key] === true });
      i.addEventListener("change", () => (s[key] = i.checked));
      return el("label", { class: "ptask", for: `ss-${key}` }, i, ` ${label}`);
    };
    const num = (key: string, label: string) => {
      const [lo, hi] = a.bounds[key] ?? [0, 999];
      const i = el("input", {
        type: "number",
        id: `ss-${key}`,
        class: "numin",
        min: String(lo),
        max: String(hi),
        value: String(s[key]),
        "aria-label": label,
      });
      i.addEventListener("change", () => (s[key] = Number(i.value)));
      return i;
    };
    const line = (...parts: (Node | string)[]) =>
      el("label", { class: "ptask" }, el("span", {}, ...parts));
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    save.addEventListener("click", async () => {
      save.disabled = true;
      const r = await api<Answer>("PUT", "/api/settings/scheduling", s);
      save.disabled = false;
      if (!r.ok) return toast("That wasn't saved", r.body.error);
      a = r.body;
      toast("Scheduling settings saved");
      draw();
    });
    const own = el("input", {
      type: "radio",
      name: "ss-see",
      id: "ss-own",
      checked: s.caregiversSee === "own",
    });
    const whole = el("input", {
      type: "radio",
      name: "ss-see",
      id: "ss-whole",
      checked: s.caregiversSee === "whole",
    });
    own.addEventListener("change", () => (s.caregiversSee = "own"));
    whole.addEventListener("change", () => (s.caregiversSee = "whole"));

    const addPattern = ib("plus", "Add a shift pattern", () => patternModal(a, null, load));
    const addFactor = ib("plus", "Add a factor (for example: comfortable with dementia care)", () =>
      factorModal(a, load),
    );
    const rule = (f: Answer["factors"][number]) => {
      const sel = el(
        "select",
        { "aria-label": `Rule: ${f.caregiver}` },
        ...a.rules.map((r) =>
          el("option", { value: r.value, selected: r.value === f.rule }, r.label),
        ),
      );
      sel.addEventListener("change", () => void changeFactor(f.id, { rule: sel.value }));
      return sel;
    };
    const weight = (f: Answer["factors"][number]) => {
      const i = el("input", {
        type: "number",
        min: "0",
        max: "5",
        value: String(f.weight),
        "aria-label": `Weight: ${f.caregiver}`,
        style: "width:60px",
      });
      i.addEventListener("change", () => void changeFactor(f.id, { weight: Number(i.value) }));
      return i;
    };
    async function changeFactor(id: string, change: Record<string, unknown>) {
      const r = await api<Answer>("PUT", `/api/settings/match-factors/${id}`, change);
      if (!r.ok) return toast("That wasn't saved", r.body.error);
      a = r.body;
      toast("Factor saved", "The next suggestions use it");
    }

    const phone = field('Office phone for "call the office"', s.officePhone, {
      placeholder: "The company's phone line",
      style: "max-width:220px",
    });
    phone.input.addEventListener("input", () => (s.officePhone = phone.input.value));
    /**
     * A list of reasons (can't work, or correcting a time): chips with edit and archive, and add
     * (saved with the settings).
     */
    function reasonsField(
      key: "cannotWorkReasons" | "correctionReasons" = "cannotWorkReasons",
      label = 'Reasons a caregiver can give for "Can\'t work"',
    ) {
      const host = el("div", { class: "field" });
      const list = s[key];
      const draw = () =>
        host.replaceChildren(
          el("label", {}, label),
          el(
            "div",
            { style: "display:flex;gap:6px;flex-wrap:wrap;align-items:center" },
            ...list.map((r, i) =>
              el(
                "span",
                { class: "chip plain", role: "group", "aria-label": `Reason: ${r}` },
                r,
                ib("pencil", `Edit the reason ${r}`, () =>
                  reasonModal("Edit the reason", r, false, (next) => {
                    list[i] = next;
                    draw();
                  }),
                ),
                ib("archive", `Archive the reason ${r} with a reason`, () =>
                  reasonModal(`Archive "${r}"`, r, true, (why) => {
                    list.splice(i, 1);
                    s.why = `Reason "${r}" archived: ${why}`;
                    draw();
                  }),
                ),
              ),
            ),
            ib(
              "plus",
              key === "correctionReasons" ? "Add a reason for correcting a time" : "Add a reason",
              () =>
                reasonModal("Add a reason", "", false, (next) => {
                  list.push(next);
                  draw();
                }),
            ),
          ),
          el("span", { class: "small muted" }, "Save keeps the list."),
        );
      draw();
      return host;
    }
    const orient = a.orientation;
    async function changeType(id: string, change: Record<string, unknown>) {
      const r = await api<Answer["orientation"]>("PUT", `/api/settings/orientation/${id}`, change);
      if (!r.ok) return toast("That wasn't saved", r.body.error);
      a = { ...a, orientation: r.body };
      toast("Orientation saved", "Each patient can change it on its Care team card");
      draw();
    }
    // Brief 1E-30: billing is each client's Bill orientation, so only paid or not here.
    const VISIT_IS: [string, string][] = [
      ["paid", "Paid"],
      ["unpaid", "Not paid"],
    ];
    const orientationCard = el(
      "section",
      { class: "card", "aria-label": "Orientation" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("people"), " Orientation"),
          el(
            "span",
            { class: "small muted" },
            "Meeting the patient before working alone. Each patient can change it on its Care team card.",
          ),
        ),
      ),
      table(
        [
          { h: "Client type", f: (t: OrientationType) => el("b", {}, t.name) },
          {
            h: "Orientation before the first visit",
            f: (t) => {
              const sel = el(
                "select",
                { "aria-label": `Orientation: ${t.name}` },
                ...orient.orientations.map((o) =>
                  el("option", { value: o.value, selected: o.value === t.orientation }, o.label),
                ),
              );
              sel.addEventListener(
                "change",
                () => void changeType(t.id, { orientation: sel.value }),
              );
              return sel;
            },
          },
          {
            h: "Orientation visit is",
            f: (t) => {
              if (t.orientation === "off") return "–";
              const sel = el(
                "select",
                { "aria-label": `Orientation visit is: ${t.name}` },
                ...VISIT_IS.map(([v, l]) =>
                  el("option", { value: v, selected: v === (t.paid ? "paid" : "unpaid") }, l),
                ),
              );
              sel.addEventListener(
                "change",
                () => void changeType(t.id, { paid: sel.value === "paid" }),
              );
              return sel;
            },
          },
        ],
        orient.types,
        undefined,
        "Orientation by client type",
      ),
      el(
        "div",
        { class: "form" },
        line(
          "Each patient should have at least ",
          num("minBackups", "Oriented backups"),
          " oriented backup (flag when missing)",
        ),
        box("backupsFirst", "When a primary can't work, suggest oriented backups first"),
        line(
          "An orientation is a shadow visit of ",
          num("orientationHours", "Hours"),
          " hours with an oriented caregiver or an RN",
        ),
      ),
    );

    // Checking and validating (brief 1D-4, mockup V["homecare/settings"] v48).
    const dc = a.docChecks;
    async function changeCheck(typeId: string, key: string, on: boolean) {
      const r = await api<DocChecks>("PUT", `/api/settings/doc-checks/${typeId}`, { key, on });
      if (!r.ok) return toast("That wasn't saved", r.body.error);
      a = { ...a, docChecks: r.body };
      toast("Documentation check saved", "The next visits checked use it");
    }
    const checkingCard = el(
      "section",
      { class: "card", "aria-label": "Checking and validating" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("checklist"), " Checking and validating"),
          el(
            "span",
            { class: "small muted" },
            "What makes a visit clean, and what happens to clean visits.",
          ),
        ),
      ),
      el("div", { class: "form" }, box("autoValidate", "Validate clean visits automatically")),
      table(
        [
          { h: "Check", f: (c: DocChecks["checks"][number]) => el("b", {}, c.label) },
          ...dc.types.map((t) => ({
            h: t.name,
            f: (c: DocChecks["checks"][number]) => {
              const i = el("input", {
                type: "checkbox",
                "aria-label": `${t.name}: ${c.label}`,
                checked: c.on[t.id] !== false,
              });
              i.addEventListener("change", () => void changeCheck(t.id, c.key, i.checked));
              return i;
            },
          })),
        ],
        dc.checks,
        undefined,
        "The documentation check per client type",
      ),
      reasonsField("correctionReasons", "Reasons for correcting a time"),
      el(
        "p",
        { class: "small muted" },
        "A correction more than ",
        num("lateCorrectionDays", "Days"),
        " days later is flagged; more than ",
        num("missedPunchPattern", "Corrections a month"),
        " missed-punch corrections for one person in a month raises a pattern flag. Save keeps these.",
      ),
    );

    slot.replaceChildren(
      el(
        "div",
        { class: "head" },
        el(
          "div",
          { class: "t" },
          el("div", { class: "eyebrow" }, "Home Care settings"),
          el("h1", {}, icon("calendar", "h1i"), "Scheduling"),
          el(
            "div",
            { class: "muted" },
            "How visits are published, confirmed, claimed and matched.",
          ),
        ),
        el("div", { class: "actions" }, save),
      ),
      el(
        "div",
        { class: "grid2" },
        el(
          "section",
          { class: "card", "aria-label": "Publishing" },
          el("h2", {}, "Publishing"),
          el(
            "div",
            { class: "form" },
            box("draftsFirst", "New visits start as drafts until published"),
            box("tellOnPublish", "Tell caregivers when visits are published (text and app)"),
            box("tellOnChange", "Tell caregivers when a published visit changes or is cancelled"),
            line(
              "Ask the office to confirm changes within ",
              num("confirmChangesHours", "Hours"),
              " hours of a visit",
            ),
          ),
        ),
        el(
          "section",
          { class: "card", "aria-label": "Confirmations and check-in" },
          el("h2", {}, "Confirmations and check-in"),
          el(
            "div",
            { class: "form" },
            box("confirmations", "Caregivers confirm or say they can't work each published visit"),
            line(
              "Remind the caregiver when not confirmed ",
              num("remindDays", "Days before"),
              " days before",
            ),
            line(
              "Tell the office when not confirmed ",
              num("officeDays", "Days before"),
              " day before",
            ),
            line(
              '"Can\'t work" needs a call to the office less than ',
              num("cannotWorkHours", "Hours"),
              " hours before (4, 24, 48…)",
            ),
            line(
              "Check-in opens ",
              num("checkInMinutes", "Minutes"),
              " minutes before the visit. Earlier is flagged.",
            ),
            // Brief 1E-30: outside these, a check-in waits for the office in To validate.
            line(
              "A check-in more than ",
              num("checkInEarlyHours", "Hours early"),
              " hours early or ",
              num("checkInLateHours", "Hours late"),
              " hours late needs the office's OK in To validate",
            ),
            // Brief 1E-19: one lateness setting for Running late? and the office's Running late item.
            line(
              "Late after ",
              num("lateMinutes", "Minutes"),
              " minutes with no check-in. The office sees it as Running late; the caregiver sees Running late? in the field app.",
            ),
            line(
              "Remind the caregiver to check out ",
              num("checkOutReminderMinutes", "Minutes after the end"),
              " minutes after the visit ends; the office fixes a missed check-out with the real time and a reason",
            ),
            line(
              "Check-in counts as at the home within ",
              num("checkInFeet", "Feet"),
              " feet; grace ",
              num("graceMinutes", "Grace minutes"),
              " minutes early or late. Outside that is flagged.",
            ),
            phone.el,
            reasonsField(),
            box(
              "phoneUnlock",
              "Phone unlock (Face ID, fingerprint or PIN) at check-in; off until each phone is set up",
            ),
          ),
        ),
        el(
          "section",
          { class: "card", "aria-label": "Open visits" },
          el("h2", {}, "Open visits"),
          el(
            "div",
            { class: "form" },
            box("openClaiming", "Caregivers can claim open visits"),
            box("officeApproves", "The office approves each claim"),
            box(
              "openMustMatchOnly",
              "Show open visits only to caregivers who fit the matching factors marked Must match",
            ),
            el(
              "label",
              { class: "ptask" },
              (() => {
                const i = el("input", {
                  type: "checkbox",
                  id: "ss-offerTop",
                  checked: s.offerTop === true,
                });
                i.addEventListener("change", () => (s.offerTop = i.checked));
                return i;
              })(),
              el(
                "span",
                {},
                " Offer a visit to the top ",
                num("offerTopN", "How many"),
                " suggestions first, then to everyone after ",
                num("offerHours", "Hours"),
                " hours",
              ),
            ),
          ),
        ),
        el(
          "section",
          { class: "card", "aria-label": "What caregivers see" },
          el("h2", {}, "What caregivers see"),
          el(
            "div",
            { class: "form" },
            el("label", { class: "ptask", for: "ss-own" }, own, " Only their own visits"),
            el(
              "label",
              { class: "ptask", for: "ss-whole" },
              whole,
              " The whole schedule (no patient details beyond first name and area)",
            ),
            el("h2", { style: "margin-top:10px" }, "Availability"),
            box("noAvailabilityOnce", "No availability on file: say so once"),
            line(
              "Changes to availability within ",
              num("availabilityChangeHours", "Hours"),
              " hours of a visit go to the office first",
            ),
            el(
              "label",
              { class: "ptask" },
              (() => {
                const i = el("input", {
                  type: "checkbox",
                  id: "ss-askUpdate",
                  checked: s.askUpdate === true,
                });
                i.addEventListener("change", () => (s.askUpdate = i.checked));
                return i;
              })(),
              el(
                "span",
                {},
                " Ask caregivers to update availability every ",
                num("askUpdateWeeks", "Weeks"),
                " weeks",
              ),
            ),
          ),
        ),
      ),
      orientationCard,
      el(
        "section",
        { class: "card", "aria-label": "Shift patterns" },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("h2", {}, "Shift patterns"),
            el(
              "span",
              { class: "small muted" },
              "Used by Schedule visits. A client can have its own; Custom hours is always allowed.",
            ),
          ),
          el("div", { class: "rowacts" }, addPattern),
        ),
        table(
          [
            { h: "Name", f: (p: Answer["patterns"][number]) => el("b", {}, p.name) },
            { h: "Start", f: (p) => clock(p.start) },
            { h: "End", f: (p) => clock(p.end) },
            { h: "Used by", f: (p) => p.usedBy },
            {
              h: "",
              f: (p) =>
                el(
                  "span",
                  { class: "vacts" },
                  ib("pencil", `Edit ${p.name}`, () => patternModal(a, p, load)),
                  ib("archive", `Archive ${p.name} with a reason`, () =>
                    archiveModal(p.name, `/api/settings/shift-patterns/${p.id}/archive`, load),
                  ),
                ),
            },
          ],
          a.patterns,
          undefined,
          "Shift patterns",
        ),
      ),
      el(
        "section",
        { class: "card mftable", "aria-label": "Matching factors" },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("h2", {}, icon("zap"), " Matching factors"),
            el(
              "span",
              { class: "small muted" },
              "Each factor pairs something about the patient with the caregiver's profile.",
            ),
          ),
          el("div", { class: "rowacts" }, addFactor),
        ),
        table(
          [
            {
              h: "About the patient (intake, care plan)",
              f: (f: Answer["factors"][number]) => f.patient,
            },
            { h: "About the caregiver (profile, app, history)", f: (f) => f.caregiver },
            { h: "Rule", f: rule },
            { h: "Weight", f: weight },
            {
              h: "",
              f: (f) =>
                ib("archive", `Archive the factor ${f.caregiver} with a reason`, () =>
                  archiveModal("this factor", `/api/settings/match-factors/${f.id}/archive`, load),
                ),
            },
          ],
          a.factors,
          undefined,
          "Matching factors",
        ),
        el(
          "p",
          { class: "small muted" },
          "Reliability looks back ",
          num("reliabilityDays", "Days"),
          " days; distance counts up to ",
          num("distanceMiles", "Miles"),
          " miles (straight line for now). Save keeps these.",
        ),
      ),
      el(
        "section",
        { class: "card", "aria-label": "Issues bar" },
        el("h2", {}, "Issues bar"),
        el(
          "div",
          { class: "grid3", style: "gap:8px" },
          ...a.issueKinds.map((k) => {
            const i = el("input", {
              type: "checkbox",
              id: `si-${k.key}`,
              checked: s.issues[k.key] !== false,
            });
            i.addEventListener("change", () => (s.issues[k.key] = i.checked));
            return el("label", { class: "ptask", for: `si-${k.key}` }, i, ` ${k.label}`);
          }),
        ),
        el(
          "p",
          { class: "small muted" },
          "The hours limit: over ",
          num("hoursLimit", "Hours a week"),
          " hours a week shows in amber on the board and on the issues bar.",
        ),
      ),
      checkingCard,
      textsCard(),
    );
  }

  /** Brief 1E-34 (mockup v92): what the texts say, one text on publish, and Email a copy. */
  function textsCard() {
    const t = a.texts;
    const copy = el("input", { type: "checkbox", id: "tx-copy", checked: t.emailCopy });
    copy.addEventListener("change", async () => {
      copy.disabled = true;
      const r = await api<{ emailCopy: boolean }>("PUT", "/api/settings/email-copy", {
        on: copy.checked,
      });
      copy.disabled = false;
      if (!r.ok) {
        copy.checked = !copy.checked;
        return toast("That wasn't saved", (r.body as { error?: string }).error);
      }
      a.texts.emailCopy = r.body.emailCopy;
      toast(r.body.emailCopy ? "Email copies on" : "Email copies off");
    });
    const rows: [string, string][] = [
      ["New visit", t.newVisit],
      [
        "Please confirm",
        `Sent only when a visit is still not confirmed ${t.remindDays} day${t.remindDays === 1 ? "" : "s"} before`,
      ],
      ["Running late", "To the patient's phone, from the office number"],
    ];
    return el(
      "section",
      { class: "card", "aria-label": "Texts to caregivers" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("comment"), " Texts to caregivers"),
          el(
            "span",
            { class: "small muted" },
            "Each text starts with her first name and the business, and links into her app.",
          ),
        ),
      ),
      el("dl", { class: "kv" }, ...rows.flatMap(([k, v]) => [el("dt", {}, k), el("dd", {}, v)])),
      el(
        "p",
        { class: "small muted", style: "margin:6px 0 10px" },
        "Publishing sends one text per caregiver, with every new visit in it.",
      ),
      el("label", { class: "ptask", for: "tx-copy" }, copy, " Email a copy of every text"),
      el("span", { class: "small muted" }, "To the person's email, when there is one."),
    );
  }
  await load();
}

/**
 * Add or edit a can't-work reason (its words), or archive one (asks why; the reason goes in the
 * history with the save).
 */
function reasonModal(title: string, had: string, archive: boolean, done: (v: string) => void) {
  const words = archive ? area("Why is it archived?", "") : field("Reason", had);
  const go = el(
    "button",
    { type: "button", class: "btn pri" },
    icon(archive ? "archive" : "check"),
    archive ? " Archive" : " Done",
  );
  const m = openModal({
    title,
    sub: archive
      ? "Caregivers stop seeing it. The reason is saved with it."
      : "Up to 40 characters. Save on the page keeps the list.",
    body: [el("div", { class: "form", style: "max-width:none" }, words.el)],
    actions: [go],
  });
  go.addEventListener("click", () => {
    const v = words.input.value.replace(/\s+/g, " ").trim();
    if (!v) return refuse(m.note, archive ? "Say why it's archived." : "Write the reason.");
    if (!archive && v.length > 40) return refuse(m.note, "Up to 40 characters.");
    m.close();
    done(v);
  });
}

/** Add or edit a shift pattern: name, start and end, and the client it's for. */
function patternModal(a: Answer, p: Answer["patterns"][number] | null, done: () => Promise<void>) {
  const name = field("Name", p?.name ?? "");
  const start = field("Start", p?.start ?? "08:00", { type: "time" });
  const end = field("End", p?.end ?? "12:00", { type: "time" });
  const client = select(
    "Used by",
    [["", "All clients"], ...a.clients.map((c) => [c.id, `${c.name} only`] as const)],
    p?.clientId ?? "",
  );
  const go = el(
    "button",
    { type: "button", class: "btn pri" },
    icon("check"),
    p ? " Save" : " Add",
  );
  const m = openModal({
    title: p ? `Edit ${p.name}` : "Add a shift pattern",
    sub: "An end at or before the start runs past midnight.",
    body: [
      el(
        "div",
        { class: "form formgrid", style: "max-width:none" },
        name.el,
        start.el,
        end.el,
        ...(p ? [] : [client.el]),
      ),
    ],
    actions: [go],
  });
  go.addEventListener("click", async () => {
    const r = p
      ? await api("PUT", `/api/settings/shift-patterns/${p.id}`, {
          name: name.input.value,
          start: start.input.value,
          end: end.input.value,
        })
      : await api("POST", "/api/settings/shift-patterns", {
          name: name.input.value,
          start: start.input.value,
          end: end.input.value,
          clientId: client.input.value || undefined,
        });
    if (!r.ok) return refuse(m.note, r.body.error);
    m.close();
    toast(p ? "Shift pattern saved" : "Shift pattern added", "Schedule visits offers it now");
    await done();
  });
}

const NEW = "__new";

/** Add a factor: pick the patient question and the caregiver field (or name new ones). */
function factorModal(a: Answer, done: () => Promise<void>) {
  const patient = select(
    "About the patient (intake, care plan)",
    [
      ["", "– (nothing about the patient)"],
      ...a.patientSides.map((x) => [x.value, x.label] as const),
      [NEW, "A new yes-or-no intake question…"],
    ],
    "",
  );
  const newQ = field("The new intake question", "", { placeholder: "Dementia care needed" });
  const caregiver = select(
    "About the caregiver (profile, app, history)",
    [
      ...a.caregiverSides.map((x) => [x.value, x.label] as const),
      [NEW, "A new yes-or-no caregiver detail…"],
    ],
    a.caregiverSides[0]?.value ?? "",
  );
  const newF = field("The new caregiver detail", "", {
    placeholder: "Comfortable with dementia care",
  });
  const rule = select(
    "Rule",
    a.rules.map((r) => [r.value, r.label] as const),
    "prefer",
  );
  const weight = field("Weight (0 to 5)", "3", { type: "number", min: "0", max: "5" });
  const shown = () => {
    newQ.el.style.display = patient.input.value === NEW ? "" : "none";
    newF.el.style.display = caregiver.input.value === NEW ? "" : "none";
  };
  patient.input.addEventListener("change", shown);
  caregiver.input.addEventListener("change", shown);
  const go = el("button", { type: "button", class: "btn pri" }, icon("plus"), " Add factor");
  const m = openModal({
    title: "Add a factor",
    sub: "Pick the patient question and the caregiver detail. The next suggestions use it.",
    body: [
      el(
        "div",
        { class: "form formgrid", style: "max-width:none" },
        patient.el,
        newQ.el,
        caregiver.el,
        newF.el,
        rule.el,
        weight.el,
      ),
      el(
        "p",
        { class: "small muted" },
        "A new intake question is asked on the patient's Intake tab; a new caregiver detail shows on each caregiver's Matching card. ",
        chip("Must match sorts the list", "info"),
      ),
    ],
    actions: [go],
    wide: true,
  });
  shown();
  go.addEventListener("click", async () => {
    const body: Record<string, unknown> = {
      rule: rule.input.value,
      weight: Number(weight.input.value),
    };
    if (patient.input.value === NEW) body.newQuestion = newQ.input.value;
    else body.patientKey = patient.input.value || null;
    if (caregiver.input.value === NEW) body.newField = newF.input.value;
    else body.caregiverKey = caregiver.input.value;
    const r = await api("POST", "/api/settings/match-factors", body);
    if (!r.ok) return refuse(m.note, r.body.error);
    m.close();
    toast("Factor added", "The next suggestions use it");
    await done();
  });
}
